import { useEffect, useRef } from 'react';

/**
 * Scroll-reveal reutilizable: anima la entrada de los hijos marcados
 * con [data-reveal] (o del propio contenedor si no hay marcados)
 * cuando cada elemento entra al viewport.
 *
 * Uso:
 *   const ref = useReveal();
 *   <section ref={ref}>
 *     <h2 data-reveal>Título</h2>
 *     <p data-reveal>Texto</p>
 *   </section>
 *
 * Implementado con IntersectionObserver + transiciones CSS (.reveal-init /
 * .reveal-in en index.css): usa geometría viva del navegador, por lo que es
 * inmune a mediciones desfasadas por transiciones de página o imágenes lazy.
 * El estado final es siempre "visible": sin soporte de IO se muestra todo.
 * Nota: por decisión del cliente las animaciones NO respetan
 * prefers-reduced-motion (su equipo lo tenía activo y veía el sitio estático).
 */
export default function useReveal(options = {}) {
    const ref = useRef(null);
    const { stagger = 80 } = options;

    useEffect(() => {
        const root = ref.current;
        if (!root) return undefined;

        const marked = root.querySelectorAll('[data-reveal]');
        const els = marked.length ? [...marked] : [root];

        if (!('IntersectionObserver' in window)) {
            els.forEach((el) => el.classList.add('reveal-init', 'reveal-in'));
            return undefined;
        }

        els.forEach((el, i) => {
            el.classList.add('reveal-init');
            el.style.transitionDelay = `${(i % 6) * stagger}ms`;
        });

        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('reveal-in');
                    io.unobserve(entry.target);
                }
            });
        }, { threshold: 0.05 });

        els.forEach((el) => io.observe(el));

        return () => io.disconnect();
    }, [stagger]);

    return ref;
}
